import { RWSService } from "@rws-framework/client";

import type {
  Course as CourseRaw,
  CourseTags,
  CreateProgramResponse,
  Program,
  Tag,
} from "./typesRaw";
import type {
  CalendarGrouping,
  CatalogCourse,
  CourseStatus,
  Course,
  Instructor,
  ProductClass,
  Section,
  SectionInstructor,
  Term,
} from "./types";
import { keyBy, uniqBy } from "src/helpers/utils";
import { Cache } from "src/helpers/cache";
import { CourseApiError, CourseApiService } from "./CourseServiceApi";
import { CourseUserService } from "./CourseServiceUser";
import { CourseFormatService } from "./CourseServiceFormat";

type Courses = {
  catalog: CatalogCourse[];
  courses: Course[];
  archived: Course[];
  terms: Term[];
  tags: Tag[];
  productClasses: ProductClass[];
};

export type CourseDetails = {
  course: Course;
  instructors: Instructor[];
  terms: Term[];
  program: Program | null;
  raw: {
    programMaster: CourseRaw;
    sectionTemplates: CourseRaw[];
    sections: CourseRaw[];
  };
};

class CourseService extends RWSService {
  static _IN_CLIENT: boolean = true;

  private cache = new Cache();
  private api = new CourseApiService();
  private user = new CourseUserService();

  public static getUserPermissionsInCourse =
    CourseUserService.getUserPermissionsInCourse;
  public getUserPermissionsInCourse =
    CourseUserService.getUserPermissionsInCourse;
  public static ensureProfessorsInfoDateFormat =
    CourseFormatService.ensureProfessorsInfoDateFormat;
  public getUser = this.user.getUser.bind(this.user);
  public editCalendar = this.api.editCourse.bind(this.api);
  public editSection = this.api.editCourse.bind(this.api);
  public editCourse = this.api.editCourse.bind(this.api);
  public createCalendar = this.api.createCalendar.bind(this.api);
  public archiveSection = this.api.archiveSection.bind(this.api);
  public archiveCalendar = this.api.archiveCalendar.bind(this.api);
  public moveSectionToCalendar = this.api.moveSectionToCalendar.bind(this.api);
  public createClass = this.api.createClass.bind(this.api);

  fetchCourses = this.cache.cached("courses", () =>
    this.uncachedFetchCourses(),
  );
  deleteCoursesCache = () => this.cache.clear("courses");

  fetchArchivedCourses = this.cache.cached("archivedcourses", () =>
    this.uncachedFetchArchivedCourses(),
  );
  deleteArchivedCoursesCache = () => this.cache.clear("archivedcourses");

  fetchCourseDetails = this.cache.cached(
    (id) => id,
    (courseId: string) => this.uncachedFetchCourseDetails(courseId),
  );
  deleteCourseDetailsCache = (id: string) => this.cache.clear(id);

  fetchCatalogCourses = this.cache.cached("catalog", () =>
    this.api.fetchCatalogCourses(),
  );
  deleteCatalogCoursesCache = () => this.cache.clear("catalog");

  fetchExistingCoursesToCopy = this.cache.cached("existing", () =>
    this.api.fetchCoursesToCopy(),
  );
  deleteExistingCoursesToCopyCache = () => this.cache.clear("existing");

  fetchTerms = this.cache.cached("terms", () => this.uncachedFetchTerms());
  deleteTermsCache = () => this.cache.clear("terms");
  fetchTags = this.cache.cached("tags", () => this.api.fetchTags());
  deleteTagsCache = () => this.cache.clear("tags");

  async uncachedFetchCourses(): Promise<Courses> {
    const [
      {
        courses: coursesRaw,
        catalog: catalogRaw,
        archived: archivedRaw,
        assistants_and_instructors,
        classes,
      },
      tags,
    ] = await Promise.all([this.api.fetchCourses(), this.fetchTags()]);
    const termTag = CourseService.findTermTag(tags);

    const courses = CourseFormatService.formatCourses(
      coursesRaw,
      assistants_and_instructors,
      termTag,
    );

    const catalog = CourseFormatService.formatCatalogCourses(
      catalogRaw,
      classes,
    );

    const archived = CourseFormatService.formatCourses(
      archivedRaw,
      assistants_and_instructors,
      termTag,
    );

    const terms = CourseFormatService.formatTerms(termTag);
    const productClasses: ProductClass[] = classes.map((c) => ({
      name: c.name,
      value: c._id,
    }));

    return {
      catalog,
      courses,
      archived,
      terms,
      tags,
      productClasses,
    };
  }

  async uncachedFetchArchivedCourses(): Promise<{
    archivedCourses: Course[];
    terms: Term[];
    productClasses: ProductClass[];
  }> {
    const [
      { archived: archivedRaw, assistants_and_instructors, classes },
      termTag,
    ] = await Promise.all([this.api.fetchArchivedCourses(), this.fetchTerms()]);
    const archivedCourses = CourseFormatService.formatCourses(
      archivedRaw,
      assistants_and_instructors,
      termTag,
    );

    const terms = CourseFormatService.formatTerms(termTag);
    const productClasses: ProductClass[] = classes.map((c) => ({
      name: c.name,
      value: c._id,
    }));

    return {
      archivedCourses,
      terms,
      productClasses,
    };
  }

  async uncachedFetchCourseDetails(courseId: string): Promise<CourseDetails> {
    const [
      { course: rawCourse, personnel, section_templates, sections, program },
      termTag,
    ] = await Promise.all([this.api.fetchCourse(courseId), this.fetchTerms()]);
    const course =
      rawCourse.section_type === "program_master"
        ? {
            ...CourseFormatService.defaultFormatCourse(
              rawCourse,
              personnel,
              termTag,
            ),
            calendarGroupings: CourseFormatService.formatCalendarGroupings(
              sections,
              section_templates,
              personnel,
              rawCourse,
            ),
          }
        : {
            ...CourseFormatService.defaultFormatCourse(
              rawCourse,
              personnel,
              termTag,
            ),
            section: CourseFormatService.formatSection(
              rawCourse,
              personnel,
              null,
            ),
          };

    const instructors: Instructor[] = personnel.map((i) => ({
      id: i._id,
      name: `${i.name} ${i.surname}`,
      email: i.email,
      firstname: i.name,
      surname: i.surname,
    }));

    const terms = CourseFormatService.formatTerms(termTag);

    return {
      course,
      instructors,
      terms,
      program,
      raw: {
        programMaster: rawCourse,
        sections,
        sectionTemplates: section_templates,
      },
    };
  }

  uncachedFetchTerms(): Promise<Tag> {
    return this.fetchTags().then(CourseService.findTermTag);
  }

  public async createProgramCourseFromCatalog(
    catalogCourseId: string,
    userId: string,
    title: string,
    openDate: string,
    tags: CourseTags,
  ): Promise<CreateProgramResponse> {
    const tagIds = CourseService.prepareCreateTagIds(tags);

    const result = await this.api.createProgramCourse({
      catalog_id: catalogCourseId,
      coordinator_id: userId,
      program_name: title,
      tags: tagIds,
    });
    const editResult = await this.editCourse(result.new_id, {
      open_date: openDate,
      title,
    });

    return { ...editResult, ...result };
  }

  public async createCourseFromExisting(
    programId: string,
    title: string,
    openDate: string,
    tags: CourseTags,
  ): Promise<CreateProgramResponse> {
    const tagIds = CourseService.prepareCreateTagIds(tags);

    const result = await this.api.createProgramCourse({
      program_template: programId,
      program_name: title,
      tags: tagIds,
    });
    const editResult = await this.editCourse(result.new_id, {
      open_date: openDate,
      title,
    });

    return { ...editResult, ...result };
  }

  public async createBlankCourse(
    userId: string,
    title: string,
    openDate: string,
    tags: CourseTags,
  ): Promise<CreateProgramResponse> {
    const tagIds = CourseService.prepareCreateTagIds(tags);

    const result = await this.api.createProgramCourse({
      coordinator_id: userId,
      program_name: title,
      tags: tagIds,
    });

    const editResult = await this.editCourse(result.new_id, {
      open_date: openDate,
      title,
    });

    return { ...editResult, ...result };
  }

  public async createBlankCatalogCourse(
    title: string,
    productClass: ProductClass,
  ): Promise<{ id: string }> {
    return this.api
      .createBlankCourse({
        title,
        class: productClass.name,
        catalog_entry: true,
      })
      .then(({ new_id }) => ({ id: new_id }));
  }

  public async createBlankStandaloneCourse(
    title: string,
    openDate: string,
    tags: CourseTags,
  ): Promise<{ id: string }> {
    const result = await this.api.createBlankCourse({
      title,
      open_date: openDate,
      tags,
    });

    const _editResult = await this.editCourse(result.new_id, {
      title,
    });

    return { id: result.new_id };
  }

  public async archiveCourse(course: Course): Promise<unknown> {
    return this.api.archiveCourse(course.id);
  }

  public async unArchiveCourse(course: Course): Promise<unknown> {
    return this.api.unarchiveCourse(course.id);
  }

  public async createSection(
    courseId: string,
    calendar: CalendarGrouping,
    instructors: Instructor[],
    userId: string,
    payload: Partial<{ title: string }>,
  ): Promise<CreateProgramResponse> {
    const instructorId = instructors[0]?.id || userId;
    const response = await this.api.createRawSection(
      courseId,
      calendar.id,
      instructorId,
      payload,
    );

    const instructorsWithRoles = instructors.map((instructor) => ({
      instructor,
      role: CourseUserService.findUserRoleInCalendarOrDefault(
        instructor.id,
        calendar,
      ),
    }));

    await this.setRawCourseInstructors(response.new_id, instructorsWithRoles);

    return response;
  }

  public async duplicateSection(
    section: Section,
    calendar: CalendarGrouping,
    title: string,
    instructors: Instructor[],
  ): Promise<unknown> {
    const instructorsWithRoles = instructors.map((instructor) => ({
      instructor,
      role: CourseUserService.findUserRoleInCalendarOrDefault(
        instructor.id,
        calendar,
      ),
    }));
    const course =
      CourseFormatService.setInstructorsAndAssistants(instructorsWithRoles);

    const result = await this.api.createSectionOrCalendarDuplicate(section.id);
    const _ = await this.api.editCourse(result.id, {
      title,
      ...course,
    });

    return result;
  }

  public async duplicateCalendar(
    calendarId: string,
    title: string,
  ): Promise<unknown> {
    const result = await this.api.createSectionOrCalendarDuplicate(calendarId);
    const _ = await this.api.renameCalendar(result.id, title);

    return result;
  }

  public editCatalogCourse(
    courseId: string,
    title: string,
    status: CourseStatus,
  ) {
    return this.api.editRawCourse(courseId, { title, status });
  }

  public duplicateCatalogCourse(
    courseId: string,
    title: string,
    productClass: ProductClass,
  ): Promise<unknown> {
    return this.api.duplicateRawCourse(courseId, {
      title,
      class: productClass.name,
      catalog_entry: true,
    });
  }

  public async duplicateStandaloneCourse(
    courseId: string,
    title: string,
    openDate: string,
    tags: CourseTags,
  ): Promise<{ new_id: string }> {
    const tagIds = CourseService.prepareCreateTagIds(tags);

    const result = await this.api.duplicateRawCourse(courseId, {
      title,
      open_date: openDate,
    });

    const _editResult = await this.editCourse(result.new_id, {
      title,
      tags: tagIds,
    });

    return result;
  }

  private static prepareCreateTagIds(tags: CourseTags) {
    return tags.filter(Boolean);
  }

  private static findTermTag(tags: Tag[]): Tag {
    const termTag = tags.find(({ key }) => key === "term");
    if (!termTag) throw new Error('"term" tag not found');

    return termTag;
  }

  public async duplicateRawSection(
    sectionId: string,
    title: string,
    instructors: string[],
    assistants: string[],
    tags: CourseTags
  ): Promise<{ new_id: string }> {
    const result = await this.api.createSectionOrCalendarDuplicate(sectionId);
    const _ = await this.api.editCourse(result.id, {
      title,
      instructors,
      assistants,
      tags,
    });

    return { new_id: result.id };
  }

  public setSectionInstructors(
    section: Section,
    calendar: CalendarGrouping,
    instructors: Instructor[],
  ): Promise<unknown> {
    const instructorsWithRoles = instructors.map((instructor) => ({
      instructor,
      role: CourseUserService.findUserRoleInCalendarOrDefault(
        instructor.id,
        calendar,
      ),
    }));

    return this.setRawCourseInstructors(section.id, instructorsWithRoles);
  }

  /**
   * This does not add/remove instructors/assistants, only changes their access right to edit the calendar
   * Calendar instructors are section instructors with calendar edit rights.
   */
  public setCalendarInstructors(
    calendar: CalendarGrouping,
    instructors: Instructor[],
  ): Promise<unknown> {
    const calendarInstructorIds = instructors.map((i) => i.id);

    const sectionRoleChanges = calendar.sections
      .map((section) => {
        const sectionInstructors = keyBy(
          section.instructors,
          (i) => i.instructor.id,
        );
        const currentInstructorsWithNewRoles: SectionInstructor[] =
          section.instructors.map((i) =>
            calendarInstructorIds.includes(i.instructor.id)
              ? { ...i, role: "instructor_edit_calendar" }
              : { ...i, role: "instructor_no_edit" },
          );
        const anyRoleChanged = currentInstructorsWithNewRoles.some(
          (i) => i.role !== sectionInstructors[i.instructor.id]?.role,
        );

        return {
          section,
          anyRoleChanged,
          instructors: currentInstructorsWithNewRoles,
        };
      })
      .filter(({ anyRoleChanged }) => anyRoleChanged);

    return Promise.all(
      sectionRoleChanges.map(({ section, instructors }) =>
        this.setRawCourseInstructors(section.id, instructors),
      ),
    );
  }

  private setRawCourseInstructors(
    sectionId: Section["id"],
    courseInstructors: SectionInstructor[],
  ): Promise<unknown> {
    const course =
      CourseFormatService.setInstructorsAndAssistants(courseInstructors);

    return this.api.editCourse(sectionId, course);
  }
}

export default CourseService.getSingleton();

export { CourseService as CourseServiceInstance };
